/**
 * API client for the Permitting SAAS backend
 */

import axios, { AxiosInstance } from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { config } from '../config';

class APIClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: config.apiEndpoint,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token to requests
    // IMPORTANT: Use access token (not ID token) for API authorization
    // Access tokens contain OAuth scopes needed for API Gateway authorization
    // ID tokens are for authentication/user info only
    this.client.interceptors.request.use(async (config) => {
      try {
        const session = await fetchAuthSession({ forceRefresh: false });
        const token = session.tokens?.accessToken?.toString();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        // Not authenticated - that's ok for public endpoints
        console.log('No auth session found');
      }
      return config;
    });

    // Add response interceptor to handle 401 errors by refreshing token and retrying
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If we get a 401 and haven't retried yet, try refreshing the token
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // Force refresh the session
            const session = await fetchAuthSession({ forceRefresh: true });
            const token = session.tokens?.accessToken?.toString();

            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return this.client(originalRequest);
            }
          } catch (refreshError) {
            // Token refresh failed - user needs to re-authenticate
            console.error('Token refresh failed:', refreshError);
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // User endpoints
  async getUserProfile() {
    const response = await this.client.get('/api/v1/user/me');
    return response.data;
  }

  // Matrix endpoints
  async generateMatrix(params: {
    project_name: string;
    state: string;
    county?: string;
    city?: string;
    power_capacity_mw: number;
    land_area_acres: number;
    project_type: string;
    has_integrated_power: boolean;
    power_sources?: Array<{
      source_type: string;
      capacity_mw: number;
    }>;
    building_size_sqft?: number;
    cooling_type?: string;
  }) {
    const response = await this.client.post('/api/v1/matrices', params);
    return response.data;
  }

  async listMatrices(limit = 50, offset = 0) {
    const response = await this.client.get('/api/v1/matrices', {
      params: { limit, offset },
    });
    return response.data;
  }

  async getMatrix(requestUuid: string) {
    const response = await this.client.get(`/api/v1/matrices/${requestUuid}`);
    return response.data;
  }

  // Subscription endpoints
  async createCheckoutSession(subscriptionType: 'monthly' | 'annual', successUrl: string, cancelUrl: string) {
    const response = await this.client.post('/api/v1/subscriptions/checkout', {
      subscription_type: subscriptionType,
      success_url: successUrl,
      cancel_url: cancelUrl,
    });
    return response.data;
  }

  async createPortalSession(returnUrl: string) {
    const response = await this.client.post('/api/v1/subscriptions/portal', {
      return_url: returnUrl,
    });
    return response.data;
  }
}

export const apiClient = new APIClient();
